import { useEffect, useRef, useState, useCallback } from 'react';
import { debounce } from 'lodash';
import { useAppSelector } from '../../../app/store/store';
import { usePriceInRange, findPriceForQuantity } from '../../../features/pricing';
import { selectTotalItemsCount } from '../../../entities/cart/model';
import { Card, FetchStatusType, Item } from '../../../types/interfaces';
import { useFavorites } from '@features/add-product-to-favorites';
import { Select } from '../../../shared/ui/select';
import SelectedProductDetail from './SelectedProductDetail';


interface SelectOption {
  label: string;
  value: number;
  key?: string;
}

interface AttributeOptionsMap {
  [attribute: string]: SelectOption[];
}

interface SelectedAttributeMap {
  [attribute: string]: SelectOption;
}

interface CartPayload {
  id: number;
  quantity: number;
  verificationItemId: string | null;
  verificationQuantity: number;
}

export interface CatalogItemSelectionFilterProps {
  product: Card;
  addToCart: (
    id: number,
    quantity: number,
    verificationItemId: string | null,
    verificationQuantity: number
  ) => void;
  removeFromCart: (id: number) => void;
  status: FetchStatusType;
}

type SelectedProduct = Item & { label: string; value: number; inCart?: boolean };

function buildCartPayload(
  product: Card,
  selectedProduct: SelectedProduct,
  quantity: number,
  isWithVerification: boolean
): CartPayload {
  return {
    id: selectedProduct.id,
    quantity,
    verificationItemId: product.verification_item?.id?.toString() ?? null,
    verificationQuantity: isWithVerification ? quantity : 0,
  };
}

export const CatalogItemSelectionFilter = ({
  product,
  addToCart,
  removeFromCart,
  status,
}: CatalogItemSelectionFilterProps) => {
  const [selectedProduct, setSelectedProduct] = useState<SelectedProduct | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedAttribute, setSelectedAttribute] = useState<SelectedAttributeMap>({});
  const [isWithVerification, setIsWithVerification] = useState(false);

  const { isFavorite, toggleFavorite } = useFavorites();
  const selectedProductId = selectedProduct?.id;
  const totalItemsInCartCount = useAppSelector(selectTotalItemsCount(selectedProductId ?? 0));

  const hasPoverkaOption = !!product.verification_item;
  const isProductSelected = !!selectedProduct;

  // Сброс выбора привязан к идентичности карточки (product.id), а НЕ к
  // самому объекту product: селектор пересоздаёт его на каждое изменение
  // корзины, и зависимость [product] сбрасывала бы выбор посреди работы
  // (синхронизацией с корзиной занят отдельный эффект ниже).
  //
  // Переход между /catalog/:id не размонтирует виджет (тот же роут), поэтому
  // при смене карточки локальный выбор надо сбрасывать вручную. Раньше из-за
  // флага firstLoad на товар-группу (items.length > 1) selectedProduct
  // оставался от предыдущей карточки — снизу показывался блок «в корзине» и
  // активная кнопка по чужому товару, хотя в выпадающем ничего не выбрано.
  useEffect(() => {
    setSelectedAttribute({});
    if (product.items.length === 1) {
      handleProductChange({ value: product.items[0].id, label: product.items[0].name });
    } else {
      setSelectedProduct(null);
      setQuantity(1);
      setIsWithVerification(false);
    }
  }, [product.id]);

  useEffect(() => () => setSelectedProduct(null), []);

  // Синхронизируем локальное состояние верхнего блока (inCart,
  // количество, флаг поверки) с реальным состоянием корзины
  // из redux (product.items[] считается селектором по cart.items).
  //
  // Таблица «В корзине» (CartSummary) пишет правки в cart напрямую,
  // минуя локальный state этого виджета, поэтому без этой синхронизации
  // изменения количества/поверки в таблице не отражались на
  // странице (и наоборот при удалении флаг inCart оставался залипшим).
  //
  // Эффект срабатывает только на смену `product`, т.е. ПОСЛЕ
  // фактического обновления cart, и не мешает вводу количества на самой
  // странице: там dispatch уходит по blur, до этого `product` не меняется.
  useEffect(() => {
    if (!selectedProductId) return;
    const live = product.items?.find((i) => i.id === selectedProductId) as
      | (Item & { inCart?: boolean })
      | undefined;
    if (!live) return;
    const liveInCart = !!live.inCart;
    setSelectedProduct((prev) =>
      prev && !!prev.inCart !== liveInCart ? { ...prev, inCart: liveInCart } : prev
    );
    if (liveInCart) {
      setQuantity((prev) => (prev !== live.quantity ? live.quantity : prev));
      setIsWithVerification((live.verification_quantity ?? 0) > 0);
    }
  }, [product, selectedProductId]);

  const priceInRange = usePriceInRange({ product: selectedProduct as Item, inputValue: quantity });

  const handleProductChange = (option: SelectOption | null) => {
    if (!option) { setSelectedProduct(null); setQuantity(1); return; }
    const found = product.items.find((p) => p.id === option.value);
    if (!found) return;
    const effectiveQty = Math.max(quantity, totalItemsInCartCount);
    const price = findPriceForQuantity(found.prices, effectiveQty);
    setSelectedProduct({ ...found, label: found.name, value: option.value, price: price || found?.prices[0]?.price });
    setQuantity(found.quantity || 1);
  };

  const newAllAttributes = product.items.reduce<AttributeOptionsMap>((acc, item) => {
    item.attributes.forEach((attr) => {
      if (!acc[attr.attribute]) acc[attr.attribute] = [];
      if (!acc[attr.attribute].some((a) => a.value === attr.id)) {
        acc[attr.attribute].push({ label: attr.value, value: attr.id, key: attr.attribute });
      }
    });
    if (acc['Длина']) acc['Длина'].sort((a, b) => parseFloat(a.label) - parseFloat(b.label));
    return acc;
  }, {});

  const filteredItems = product.items.filter((item) =>
    Object.entries(selectedAttribute).every(([key, sel]) =>
      item.attributes.some((a) => a.attribute === key && a.id === sel.value)
    )
  );

  useEffect(() => {
    if (filteredItems.length === 1) handleProductChange({ label: filteredItems[0].name, value: filteredItems[0].id });
  }, [selectedAttribute]);

  const dispatchCartUpdate = useCallback(
    (payload: CartPayload) => {
      addToCart(payload.id, payload.quantity, payload.verificationItemId, payload.verificationQuantity);
    },
    [addToCart]
  );

  // Дебаунсим запись количества в корзину так же, как это делает таблица
  // (useCartItemQuantity), чтобы не слать запрос на каждый клик +/- и каждый
  // символ ввода. Пишем только для уже добавленного товара; для не-в-корзине
  // количество остаётся локальным до явного «Добавить в корзину».
  const debouncedCartUpdate = useRef(
    debounce((payload: CartPayload) => dispatchCartUpdate(payload), 500)
  ).current;

  useEffect(() => () => debouncedCartUpdate.cancel(), [debouncedCartUpdate]);

  // Кнопки +/- и ручной ввод количества в SelectedProductDetail идут сюда.
  // Раньше диспатч в корзину уходил только по blur инпута, поэтому клики
  // +/- (они не дают blur) меняли количество вверху, но не в таблице.
  const handleQuantityChange = (value: number) => {
    const next = value < 1 ? 1 : value;
    setQuantity(next);
    if (selectedProduct?.inCart) {
      debouncedCartUpdate(
        buildCartPayload(product, selectedProduct, next, isWithVerification)
      );
    }
  };

  const handleBlur = () => {
    if (quantity < 1) setQuantity(1);
    // Досрочно сбрасываем отложенный диспатч, чтобы не ждать debounce.
    debouncedCartUpdate.flush();
  };

  const addingToCart = () => {
    if (!selectedProduct) return;
    setSelectedProduct((prev) => (prev ? { ...prev, inCart: true, quantity } : null));
    dispatchCartUpdate(buildCartPayload(product, selectedProduct, quantity, isWithVerification));
  };

  const removingFromCart = (id: number) => {
    setSelectedProduct((prev) => (prev ? { ...prev, inCart: false, quantity: 1 } : null));
    removeFromCart(id);
  };

  const hasSelect = product.items.length > 1;

  return (
    <div className="flex flex-col gap-4 w-full">
      {hasSelect && (
        <Select
          label="Выберите продукт"
          options={filteredItems.map((p) => ({ value: p.id, label: p.name }))}
          onSelected={handleProductChange}
          optionSelected={selectedProduct}
          placeholder="Выбрать продукт"
        />
      )}

      {Object.entries(newAllAttributes).map(([attrName, options], attrIndex) => (
        <div
          key={attrName}
          className={`flex flex-col gap-3${hasSelect && attrIndex === 0 ? ' mt-[41px]' : ''}`}
        >
        
          <span className="text-sm font-medium text-[#6E7173]">{attrName}</span>
          <div className="flex flex-wrap gap-2">
            {options.map((opt) => {
              const isActive = selectedAttribute[attrName]?.value === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => {
                    if (isActive) {
                      setSelectedAttribute((prev) => { const { [attrName]: _, ...rest } = prev; return rest; });
                    } else {
                      setSelectedAttribute((prev) => ({ ...prev, [attrName]: opt }));
                    }
                  }}
                  className={`min-w-[56px] h-[42px] px-3 rounded-lg text-sm font-medium border cursor-pointer transition-all flex items-center justify-center ${
                    isActive
                      ? 'bg-[#e3282a] border-[#e3282a] text-white'
                      : 'bg-[#F5F6FA] border-transparent text-[#141414] hover:border-[#e3282a] hover:text-[#e3282a]'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* С поверкой. Паспорт с 09.09.2026 входит в поверку и отдельной
          позицией к датчику не продаётся — он вынесен в свою карточку каталога. */}
      {hasPoverkaOption && (
        <div className={`flex flex-col gap-2 transition-opacity ${!isProductSelected ? 'opacity-40 pointer-events-none' : ''}`}>
          <span className="text-sm font-semibold text-[#2d2c2c]">С поверкой</span>
          <div className="flex gap-4">
            {(['Да', 'Нет'] as const).map((val) => {
              const active = val === 'Да' ? isWithVerification : !isWithVerification;
              return (
                <label key={val} className={`flex items-center gap-2 ${isProductSelected ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                  <input
                    type="radio"
                    name="verification"
                    checked={active}
                    disabled={!isProductSelected}
                    className="accent-[#e3282a] w-4 h-4 cursor-pointer"
                    onChange={() => {
                      const checked = val === 'Да';
                      setIsWithVerification(checked);
                      if (selectedProduct?.inCart) {
                        dispatchCartUpdate(buildCartPayload(
                          product, selectedProduct, quantity, checked
                        ));
                      }
                    }}
                  />
                  <span className={`text-sm ${active ? 'font-semibold text-[#2d2c2c]' : 'text-gray-500'}`}>{val}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {selectedProduct ? (
        <SelectedProductDetail
          status={status}
          product={selectedProduct}
          pricesInRange={priceInRange}
          inputValue={quantity}
          setInputValue={handleQuantityChange}
          handler={handleBlur}
          addingItemToCart={addingToCart}
          removeFromCart={removingFromCart}
          inCart={selectedProduct?.inCart ?? false}
          isFavorite={isFavorite(product.id)}
          onToggleFavorite={() => toggleFavorite(product.id)}
          // Цена поверки берётся из product (Card) — там лежит
          // verification_item с собственными prices[]. findPriceForQuantity
          // с тем же quantity, что и у датчика, потому что продаём 1:1
          // (см. buildCartPayload).
          verificationUnitPrice={
            product.verification_item
              ? findPriceForQuantity(
                  product.verification_item.prices,
                  quantity
                )
              : null
          }
          withVerification={isWithVerification}
        />
      ) : null}


      {selectedProduct && !selectedProduct.price && (
        <p className="text-xs text-amber-500 py-2">
          Цена не указана — свяжитесь с менеджером для информации
        </p>
      )}
    </div>
  );
};
